"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RequestAQuote() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <section id="request-a-quote" ref={ref} className="bg-background py-16 md:py-24">
      <div className="container mx-auto max-w-2xl px-4">
        <div className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-3">
              Request a Quote
            </h2>
            <p className="text-muted-foreground text-base md:text-lg">
              Fill out the form below and we'll get back to you with a competitive quote
            </p>
          </div>

          <FormProvider className="bg-white rounded-sm border border-border shadow-soft p-8" formId="request_quote_form_home" sectionName="request-a-quote" defaultValues={{
          name: "",
          company_name: "",
          email: "",
          phone: "",
          product: "",
          quantity: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 100
          },
          company_name: {
            required: true,
            minLength: 2,
            maxLength: 200
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: false
          },
          product: {
            required: true,
            minLength: 2,
            maxLength: 200
          },
          quantity: {
            required: true,
            min: 1
          },
          message: {
            required: false,
            maxLength: 1000
          }
        }} submitText="Submit Quote Request" successMessage="Thank you! We've received your quote request and will respond within 24 hours.">
            <QuoteForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-accent-light border-l-4 border-accent rounded-sm">
        <p className="text-foreground font-medium mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full sm:w-auto">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 100,
          message: "Name must not exceed 100 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="border-b-2 border-t-0 border-l-0 border-r-0 rounded-none focus-visible:border-accent focus-visible:ring-0 px-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company_name" rules={{
        required: "Company name is required",
        minLength: {
          value: 2,
          message: "Company name must be at least 2 characters"
        },
        maxLength: {
          value: 200,
          message: "Company name must not exceed 200 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Company / Organization</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" className="border-b-2 border-t-0 border-l-0 border-r-0 rounded-none focus-visible:border-accent focus-visible:ring-0 px-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="border-b-2 border-t-0 border-l-0 border-r-0 rounded-none focus-visible:border-accent focus-visible:ring-0 px-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555 1234" className="border-b-2 border-t-0 border-l-0 border-r-0 rounded-none focus-visible:border-accent focus-visible:ring-0 px-0" {...field} />
              </FormControl>
              <FormDescription>Optional - we'll use this for quicker follow-up</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="product" rules={{
        required: "Product is required",
        minLength: {
          value: 2,
          message: "Product must be at least 2 characters"
        },
        maxLength: {
          value: 200,
          message: "Product must not exceed 200 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Product / Commodity</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Wheat, Steel Rods, etc." className="border-b-2 border-t-0 border-l-0 border-r-0 rounded-none focus-visible:border-accent focus-visible:ring-0 px-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="quantity" rules={{
        required: "Quantity is required",
        min: {
          value: 1,
          message: "Quantity must be at least 1"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Estimated Quantity</FormLabel>
              <FormControl>
                <Input type="number" placeholder="1000" className="border-b-2 border-t-0 border-l-0 border-r-0 rounded-none focus-visible:border-accent focus-visible:ring-0 px-0" {...field} />
              </FormControl>
              <FormDescription>Enter approximate quantity needed (units)</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        maxLength: {
          value: 1000,
          message: "Message must not exceed 1000 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Additional Details</FormLabel>
              <FormControl>
                <textarea placeholder="Any specific requirements, delivery timeline, etc." rows={4} className="border-b-2 border-t-0 border-l-0 border-r-0 rounded-none focus-visible:border-accent focus-visible:ring-0 px-0 w-full resize-none outline-none bg-transparent placeholder:text-muted-foreground text-foreground" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-sm">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary-hover text-white font-display font-semibold py-6 rounded-sm shadow-soft hover:shadow-medium hover:-translate-y-0.5 transition-all duration-[var(--duration-fast)]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Submitting Request...
            </> : 'Submit Quote Request'}
        </Button>
      </div>
    </Form>;
}